<script setup lang="ts">
import { just, nothing, type Maybe } from "../lib/maybe";
import { computed } from "vue";
import { useTemplateRef } from "vue";
import type { ZwMetadata } from "../lib/zwConfig";
import ProjectSettingsCoverSection from "./ProjectSettingsCoverSection.vue";
import ZwFieldRow from "./ui/ZwFieldRow.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwTextarea from "./ui/ZwTextarea.vue";

const meta = defineModel<ZwMetadata>("meta", { required: true });
const coverImage = defineModel<string>("coverImage", { required: true });

const props = defineProps<{
  /** Folder name shown when metadata title is empty. */
  folderName: string;
  /** Project root path — required for cover image import. */
  rootPath: Maybe<string>;
  coverPreviewSrc: string;
}>();

const titlePlaceholder = computed(() =>
  props.folderName.length > 0 ? props.folderName : "Untitled project",
);

const titleInput = useTemplateRef<InstanceType<typeof ZwInput>>("titleInput");

/** Replace the whole meta object so TEA `shallowRef` parents see a new value. */
function patchMeta(patch: Partial<ZwMetadata>): void {
  meta.value = { ...meta.value, ...patch };
}

defineExpose({
  focus: () => titleInput.value?.focus(),
});
</script>

<template>
  <section
    class="project-settings-group"
    aria-labelledby="settings-book-heading"
  >
    <h3 id="settings-book-heading" class="project-settings-subhead">Details</h3>
    <div class="details-book">
      <div class="details-book-top">
        <ZwInput
          :id="nothing"
          ref="titleInput"
          :model-value="meta.title"
          :label="just('Title')"
          :placeholder="titlePlaceholder"
          autocomplete="off"
          @update:model-value="patchMeta({ title: $event })"
        />
        <ZwInput
          :id="nothing"
          :model-value="meta.subtitle"
          :label="just('Subtitle')"
          autocomplete="off"
          @update:model-value="patchMeta({ subtitle: $event })"
        />
        <ZwInput
          :id="nothing"
          :model-value="meta.author"
          :label="just('Author')"
          placeholder="Name or names"
          autocomplete="name"
          @update:model-value="patchMeta({ author: $event })"
        />
        <ZwFieldRow>
          <ZwInput
            :id="nothing"
            :model-value="meta.language"
            :label="just('Language')"
            placeholder="en"
            autocomplete="off"
            spellcheck="false"
            @update:model-value="patchMeta({ language: $event })"
          />
          <ZwInput
            :id="nothing"
            :model-value="meta.pubDate"
            :label="just('Publication date')"
            placeholder="2026 or 2026-03-15"
            autocomplete="off"
            @update:model-value="patchMeta({ pubDate: $event })"
          />
        </ZwFieldRow>
      </div>
      <div class="details-book-cover">
        <ProjectSettingsCoverSection
          v-model:cover-image="coverImage"
          :root-path="rootPath"
          :preview-src="coverPreviewSrc"
        />
      </div>
      <div class="details-book-rest">
        <ZwTextarea
          :id="nothing"
          :model-value="meta.description"
          :label="just('Description')"
          placeholder="Short synopsis for catalogs and exports"
          @update:model-value="patchMeta({ description: $event })"
        />
        <ZwInput
          :id="nothing"
          :model-value="meta.subject"
          :label="just('Subject / keywords')"
          placeholder="Comma-separated topics"
          autocomplete="off"
          @update:model-value="patchMeta({ subject: $event })"
        />
      </div>
    </div>
  </section>

  <section
    class="project-settings-group"
    aria-labelledby="settings-publishing-heading"
  >
    <h3 id="settings-publishing-heading" class="project-settings-subhead">
      Publishing &amp; export
    </h3>
    <ZwInput
      :id="nothing"
      :model-value="meta.publisher"
      :label="just('Publisher')"
      autocomplete="organization"
      @update:model-value="patchMeta({ publisher: $event })"
    />
    <ZwInput
      :id="nothing"
      :model-value="meta.rights"
      :label="just('Copyright / rights')"
      placeholder="© Year Name"
      autocomplete="off"
      @update:model-value="patchMeta({ rights: $event })"
    />
    <p class="project-settings-hint">
      Leave blank to use the author and year on your copyright page.
    </p>
    <ZwFieldRow>
      <ZwInput
        :id="nothing"
        :model-value="meta.isbn"
        :label="just('ISBN')"
        placeholder="Optional"
        autocomplete="off"
        spellcheck="false"
        @update:model-value="patchMeta({ isbn: $event })"
      />
      <ZwInput
        :id="nothing"
        :model-value="meta.lccn"
        :label="just('Library of Congress (LCCN)')"
        placeholder="Optional"
        autocomplete="off"
        spellcheck="false"
        @update:model-value="patchMeta({ lccn: $event })"
      />
    </ZwFieldRow>
  </section>
</template>

<style>
/* The cover sits left of the top fields; on narrow screens it drops below
 * Subject / keywords as its own block. */
.details-book {
  display: grid;
  grid-template-columns: 9.5rem minmax(0, 1fr);
  grid-template-areas:
    "cover top"
    "rest rest";
  gap: 0.85rem 1.25rem;
}

.details-book-top,
.details-book-rest,
.details-book-cover {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
}

.details-book-top {
  grid-area: top;
}

.details-book-rest {
  grid-area: rest;
}

.details-book-cover {
  grid-area: cover;
}

@media (max-width: 520px) {
  .details-book {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "top"
      "rest"
      "cover";
  }

  .details-book-cover {
    padding-top: 1.1rem;
    border-top: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
  }

  .details-book-cover .cover-tile {
    max-width: 12rem;
  }
}

html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"]) .details-book {
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    "top"
    "rest"
    "cover";
}

html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"]) .details-book-cover {
  padding-top: 1.1rem;
  border-top: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
}

html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"])
  .details-book-cover
  .cover-tile {
  max-width: 12rem;
}
</style>
